import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import SotbitInput from '../index.js';
import { DEFAULT_INPUT_PROPERTIES } from '@/components/input/constants.js';
import { EVENT_MASK_ACCEPT, EVENT_MASK_COMPLETE } from '@/components/input/masked/constants.js';
import { createInputMask } from '@/directives/InputMaskDirective.js';
import { getMessage } from '@/localization.js';
import messages from './localization.json';

export default class SotbitInputMasked extends SotbitInput {
    static properties = {
        ...DEFAULT_INPUT_PROPERTIES,
        clearable: { type: Boolean },
        mask: { type: String },
        size: { type: String },
    };

    get value() {
        if (this._mask) {
            if (!this._mask.unmaskedValue) {
                return '';
            }

            return this._mask.value;
        }

        return this.inputRef.value?.value || this._pendingValue || '';
    }

    set value(value) {
        this._pendingValue = String(value ?? '').trim();

        if (this._mask) {
            this._mask.value = this._pendingValue;
        } else if (this.inputRef.value instanceof HTMLInputElement) {
            this.inputRef.value.value = this._pendingValue;
        }

        this._internals?.setFormValue(this.value);

        if (this.inputRef?.value) {
            this._validationController.validate();
        }

        if (this.error) {
            this.checkValidity(false);
        }
    }

    _syncMaskValidity() {
        const isComplete = Boolean(this._mask?.masked.isComplete);
        const isEmpty = !this._mask?.unmaskedValue;

        this.inputRef.value?.setCustomValidity(
            isComplete || (!this.required && isEmpty)
                ? ''
                : getMessage(messages, 'patternMismatchErrorMessage')
        );
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        queueMicrotask(() => {
            if (!this.isConnected) {
                this._mask?.destroy();
            }
        });
    }

    firstUpdated() {
        super.firstUpdated();

        if (!this.inputRef.value || !this.mask) {
            return;
        }

        this._mask = createInputMask(this.inputRef.value, {
            mask: this.mask,
            lazy: false,
        });

        this._mask.on('accept', () => {
            this._internals?.setFormValue(this.value);
            this._syncMaskValidity();
            this._validationController.validate();

            this.dispatchEvent(
                new Event(EVENT_MASK_ACCEPT, {
                    bubbles: true,
                    composed: true,
                })
            );
        });

        this._mask.on('complete', () => {
            this.dispatchEvent(
                new Event(EVENT_MASK_COMPLETE, {
                    bubbles: true,
                    composed: true,
                })
            );
        });

        if (this._pendingValue) {
            this.value = this._pendingValue;
        }

        this._syncMaskValidity();
        this._validationController.validate();
    }
    render() {
        return html`
            <sotbit-input-style
                size="${this.size || nothing}"
                error-text="${this.errorText || nothing}"
                helper-text="${this.helperText || nothing}"
                label="${this.label || nothing}"
                part="input-style"
                ?error="${this.error}"
                ${ref(this.styleRef)}
                @change="${this._handleChange}"
                @keydown="${this._handleKeydown}"
            >
                <input
                    type="text"
                    name="${this.name || nothing}"
                    slot="input"
                    part="input"
                    placeholder="${this.placeholder}"
                    ?disabled="${this.disabled}"
                    ?readonly="${this.readonly}"
                    ?required="${this.required}"
                    ${ref(this.inputRef)}
                />
            </sotbit-input-style>
        `;
    }
}
